import { useState, useEffect } from "react";
import Breadcrumb from "./Breadcrumb";
import { Calendar, GeoAlt } from "react-bootstrap-icons";
import { CiCalendar, CiLocationOn } from "react-icons/ci";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";
import {
  GrantAccess,
  TicketDetailsUrl,
} from "../services/adminApiService/venueManagerApi/venueManagerApi";
import RemarkModal from "./RemarkModal";
import { CgDanger } from "react-icons/cg";
import { IoCheckmarkCircleOutline } from "react-icons/io5";
import { useLocation } from "react-router-dom";
import PageTitle from "../jsx/layouts/PageTitle";

function TicketDetails() {
  const [bookingId, setBookingId] = useState("");
  const location = useLocation();

  const pathSegments = location.pathname.split("/");
  const id = pathSegments[pathSegments.length - 1];

  const isNumericId = !isNaN(Number(id));

  const [loading, setLoading] = useState(false);
  const [bookingDetails, setBookingDetails] = useState({});
  const [remarkModal, setRemarkModal] = useState(false);

  const statusConfig = {
    Completed: {
      label: "Successful",
      bg: "#eaf5ec",
      color: "#2e7d32",
    },
    Pending: {
      label: "Pending",
      bg: "#fff4e5",
      color: "#a85c00",
    },
    Failed: {
      label: "Failed",
      bg: "#f8d7da",
      color: "#a10000",
    },
    Canceled: {
      label: "Canceled",
      bg: "#fce4ec",
      color: "#ad1457",
    },
    Inprocess: {
      label: "InProcess",
      bg: "#e3f2fd",
      color: "#1565c0",
    },
  };

  const status = statusConfig[bookingDetails?.bookingStatus] || {
    label: bookingDetails?.bookingStatus,
    bg: "#e0e0e0",
    color: "#000000",
  };

  const formatDateRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    const sd = start.getDate();
    const sm = start.toLocaleString("en-GB", { month: "short" });
    const sy = start.getFullYear();

    const ed = end.getDate();
    const em = end.toLocaleString("en-GB", { month: "short" });
    const ey = end.getFullYear();

    // Same day → "30 Jan 2026"
    if (sd === ed && sm === em && sy === ey) {
      return `${sd} ${sm} ${sy}`;
    }

    // Same year → "30 Jan - 31 Jan 2026"
    if (sy === ey) {
      return `${sd} ${sm} - ${ed} ${em} ${ey}`;
    }

    // Different years → "30 Dec 2025 - 02 Jan 2026"
    return `${sd} ${sm} ${sy} - ${ed} ${em} ${ey}`;
  };

  const fetchTicketDetails = async (id) => {
    setLoading(true);
    try {
      const response = await TicketDetailsUrl(id);
      // console.log("API response:", response.data); // See the structure

      // If response.data is the array you want to map

      setBookingDetails(response.data.bookingDetails[0]);
      setBookingId(response.data.bookingDetails.booking_id);

      // If it's nested, e.g. response.data.bookings
      // setBookingDetails(response.data.bookings);
    } catch (error) {
      console.error("Booking fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTicketDetails(id);
  }, [remarkModal]);

  function formatDateToMonthDayYear(dateString) {
    if (!dateString) return "";

    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date)) return "";

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long", // full month name: "May"
      day: "numeric",
    });
  }

  function convertTo12Hour(time24) {
    if (!time24) return "";

    const [hourStr, minuteStr] = time24.split(":");
    let hour = parseInt(hourStr, 10);
    const minute = minuteStr;

    const ampm = hour >= 12 ? "PM" : "AM";
    hour = hour % 12;
    if (hour === 0) hour = 12;

    return `${hour}:${minute} ${ampm}`;
  }

  const handleClose = () => {
    setRemarkModal(false);
  };

  const handleCancel = () => {
    window.location.href = `/event-booking`;
  };

  const handleGrantAccess = async (id) => {
    setLoading(true);

    try {
      const response = await GrantAccess(id);
      console.log("API Response:", response);

      if (response?.data?.success) {
        Swal.fire({
          icon: "success",
          title: "Access Granted",
          text: response?.data?.message || "Access granted successfully.",
          confirmButtonColor: "#753FC9",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Failed",
          text: response?.data?.message || "Unable to grant access.",
          showCloseButton: true,
        });
      }
    } catch (error) {
      console.error("Grant access error:", error.response || error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error?.response?.data?.message ||
          "Something went wrong. Please try again.",
        showCloseButton: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // console.log(typeof (bookingDetails?.isVerified), "is")
  return (
    <>
      <h1 className="mb-3 fs-2 ">
        <PageTitle motherMenu={"Ticketing"} activeMenu={"Ticket Details"} />
      </h1>
      <div className="booking-details-container">
        <div className="d-flex justify-content-center mb-4">
          {bookingDetails?.bookingStatus?.toLowerCase() === "completed" && (
            bookingDetails?.isVerified === true && (
              <div
                className="alert-box d-flex align-items-center p-2"
                style={{ backgroundColor: "#edf6f0", borderRadius: "15px" }}
              >
                <IoCheckmarkCircleOutline
                  style={{
                    color: "rgba(74, 165, 100, 1)",
                    fontSize: "25px",
                    padding: "0px 5px 5px 0px",
                  }}
                />
                <span
                  style={{ color: "rgba(74, 165, 100, 1)", fontSize: "15px" }}
                >
                  Payment Successful, Grant Access.
                </span>
              </div>
          ))}
        </div>
        <div className="booking-card">
          <h1 className="title">View Event</h1>
          <Breadcrumb
            breadCrumbPayload={[
              { title: "Home", url: "", isActive: false },
              { title: "Customer Bookings", url: "", isActive: false },
              { title: "USP01BALLU", url: "", isActive: true },
            ]}
          />

          <h2 className="section-title">View Event</h2>

          <div className="booking-info-layout">
            <div className="left-section">
              <img
                src="/logo/Play_Ground.png"
                alt="Play Ground"
                className="playground-image"
              />

              <div className="venue-info">
                <div className="d-flex flex-column gap-1">
                  <h3 className="mb-0">{bookingDetails?.eventName || ""}</h3>

                  <p className="text-muted d-flex align-items-center mb-0">
                    <GeoAlt size={16} />
                    <span className="ms-2">
                      {bookingDetails?.venueAddress || ""}
                    </span>
                  </p>

                  <p
                    className="d-flex align-items-center mb-0"
                    style={{
                      color: "#660000",
                      cursor: "pointer",
                      fontWeight: 400,
                    }}
                  >
                    <GeoAlt size={16} color="#660000" />
                    <span className="ms-2">Get Directions</span>
                  </p>
                </div>

                <div className="details-grid">
                  <div>
                    <h4>Ticket Details</h4>
                    <p>
                      <strong>Name:</strong> {bookingDetails?.userName || "N/A"}
                    </p>
                    <p>
                      <strong>Contact Number:</strong> +91{" "}
                      {bookingDetails?.contactNo || "N/A"}
                    </p>
                    <p>
                      <strong>Email Address:</strong>{" "}
                      {bookingDetails?.userEmail || "N/A"}
                    </p>

                    <p>
                      <strong>Date:</strong>{" "}
                      {formatDateRange(
                        bookingDetails?.startDate,
                        bookingDetails?.endDate,
                      )}
                    </p>
                    {/* {bookingDetails?.bookingStatus === "Completed" && (
                      <p>
                        <strong>Payment Gateway:</strong>{" "}
                        {bookingDetails?.payment_plateform_type || ""}
                      </p>
                    )} */}
                    {/* {bookingDetails?.bookingStatus === "Completed" && (
                      <p>
                        <strong>Payment Method:</strong>{" "}
                        {bookingDetails?.payment_method || ""}
                      </p>
                    )} */}
                  </div>
                  <div>
                    <p
                      style={{
                        marginTop: "32px",
                      }}
                    >
                      <strong>No. of Tickets:</strong>{" "}
                      {bookingDetails?.noOfTickets}
                    </p>
                    {/* <p>
                      <strong>Created On:</strong>{" "}
                      {formatDateToMonthDayYear(bookingDetails?.createdAt) ||
                        ""}
                    </p> */}

                    {/* <p>
                      <strong>Time Slot:</strong>{" "}
                      {(bookingDetails?.slot_from_time || "") +
                        "-" +
                        (bookingDetails?.slot_to_time || "")}
                    </p> */}
                    {/* <p>
                      <strong>Sport:</strong>{" "}
                      {bookingDetails?.sport?.sport_name || ""}
                    </p> */}
                    <p>
                      <strong>Amount:</strong> ₹{" "}
                      {bookingDetails?.finalPrice || ""}
                    </p>
                    <p>
                      <strong>Payment:</strong>{" "}
                      <span
                        style={{
                          fontWeight: 500,
                          padding: "5px 10px",
                          borderRadius: "15px",
                          backgroundColor: status.bg,
                          color: status.color,
                        }}
                      >
                        {status.label}
                      </span>
                    </p>
                    <p>
                      <strong>Booking Ticket:</strong>{" "}
                      <span
                        style={{
                          fontWeight: 500,
                          // fontSize: "14px",
                          padding: "5px 10px 5px 10px",
                          borderRadius: "15px",
                          backgroundColor:
                            bookingDetails?.bookingStatus === "Completed"
                              ? "#eaf5ec"
                              : bookingDetails?.bookingStatus === "Pending"
                                ? "#fff4e5"
                                : bookingDetails?.bookingStatus === "Failed"
                                  ? "#f8d7da"
                                  : bookingDetails?.bookingStatus === "Canceled"
                                    ? "#fce4ec"
                                    : bookingDetails?.bookingStatus ===
                                        "Inprocess"
                                      ? "#e3f2fd"
                                      : "#e0e0e0", // fallback
                          color:
                            bookingDetails?.bookingStatus === "Completed"
                              ? "#2e7d32"
                              : bookingDetails?.bookingStatus === "Pending"
                                ? "#a85c00"
                                : bookingDetails?.bookingStatus === "Failed"
                                  ? "#a10000"
                                  : bookingDetails?.bookingStatus === "Canceled"
                                    ? "#ad1457"
                                    : bookingDetails?.bookingStatus ===
                                        "Inprocess"
                                      ? "#1565c0"
                                      : "#000000", // fallback
                        }}
                      >
                        {bookingDetails?.bookingStatus === "Pending"
                          ? "Pending"
                          : bookingDetails?.bookingStatus === "Completed"
                            ? "Successful"
                            : bookingDetails?.bookingStatus === "Failed"
                              ? "Failed"
                              : bookingDetails?.bookingStatus === "Canceled"
                                ? "Canceled"
                                : bookingDetails?.bookingStatus === "Inprocess"
                                  ? "InProcess"
                                  : bookingDetails?.bookingStatus}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {bookingDetails?.bookingStatus == "Completed" && (
              <div className="right-section">
                <h3 className="fs-4">{bookingDetails?.eventName || ""}</h3>
                <h3>{bookingDetails?.venue?.venue_name || ""}</h3>
                <p className="text-muted my-1">
                  <GeoAlt size={16} />
                  <span className="ms-2">
                    {bookingDetails?.venueAddress || ""}
                  </span>
                </p>

                <div
                  className="mb-3 text-muted"
                  style={{ fontSize: "0.85rem" }}
                >
                  <CiCalendar size={16} strokeWidth={1} />
                  <span className="location-style ms-1">
                    {" "}
                    {formatDateRange(
                      bookingDetails?.startDate,
                      bookingDetails?.endDate,
                    )}
                  </span>
                </div>

                <div className="booking-id-box">
                  <span>#Ticket ID</span>
                  <br />
                  <span className="booking-id">
                    {bookingDetails?.ticketId || ""}
                  </span>
                </div>

                {bookingDetails?.qrCode && (
                  <div className="qr-section d-flex justify-content-center align-items-center">
                    <img
                      src={bookingDetails?.qrCode}
                      alt="QR Code"
                      className="qr-code"
                    />
                  </div>
                )}

                <p className="field-display">
                  <strong>Ticket </strong>
                  {bookingDetails?.noOfTickets || ""}
                </p>

                <div className=" d-flex align-items-center gap-3">
                  <img
                    src={
                      bookingDetails?.eventImage ||
                      "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQxOX4mkcW8pH9FbpI9rTBkokiMxSY2GJ3eyw&s"
                    }
                    alt="avatar"
                    style={{
                      width: "50px",
                      height: "50px",
                      // borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />

                  <div>
                    <p className="avatar-name mb-0">
                      {bookingDetails?.userName || ""}
                    </p>
                    <p className="avatar-email mb-0">
                      {bookingDetails?.userEmail || ""}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        {bookingDetails?.bookingStatus == "Completed" &&
          bookingDetails?.isVerified === false && (
            <div className="d-flex align-items-start justify-content-start gap-3">
              <button className="custom-button-cancel" onClick={handleCancel}>
                Cancel
              </button>

              <button
                className="custom-button-continue"
                onClick={() => {
                  console.log("Button clicked", bookingDetails?.id);
                  handleGrantAccess(bookingDetails?.id);
                }}
              >
                Grant Access
              </button>
            </div>
          )}
      </div>
    </>
  );
}

export default TicketDetails;
